import { ComponentRef, inject, Injectable, Injector, OnDestroy, Type } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';

import { BaasI18n } from '@lvflanders/bar';

import { BaasLoadingOptions, createDefaultBaasLoadingOptions } from '../../baas-loading/baas-loading-options.type';
import { BarActionButtonOptions } from '../../bar-action-button/bar-action-button-options.model';
import { BaasExternalLinkGroupOptions } from '../baas-external-link-group-options.type';
import { BarAppMenuItem } from '../bar-app-menu-item.type';
import { BarDeviceType } from '../bar-device-type.enum';
import { _BarInternalLayoutService } from '../bar-internal-layout.service';
import { BarLayoutInternalGvkService } from '../bar-layout-internal-gvk.service';
import { BarLayoutSection } from '../bar-layout-section.enum';
import { BarLayoutSectionInjection } from '../bar-layout-section-injection.model';
import { BarMenuState } from '../bar-menu-state.type';
import { BaasOffcanvasOptions } from '../offcanvas/baas-offcanvas-options';
import { BarMenuItemOptions } from './bar-layout-menu/bar-menu-item-options.model';
import { BarResetLayoutOptions } from './bar-reset-layout-options.model';

/** @deprecated Use BaasLayout with signals in conjunction with &lt;baas-layout&gt; with OnPush change detection. Public BAR layout service */
@Injectable({ providedIn: 'root' })
export class BarLayoutService implements OnDestroy {
    readonly #translate = inject(BaasI18n);
    readonly #injector = inject(Injector);
    readonly #gvk = inject(BarLayoutInternalGvkService);

    /** Whether the layout chrome should be hidden showing only the application content. */
    readonly hidden$ = new BehaviorSubject(false);
    readonly helpLink$: BehaviorSubject<string> = new BehaviorSubject(null);
    readonly title$: BehaviorSubject<string> = new BehaviorSubject(null);
    readonly subTitle$: BehaviorSubject<string> = new BehaviorSubject(null);
    readonly mainHeader$: BehaviorSubject<string> = new BehaviorSubject(null);
    readonly documentTitle$: BehaviorSubject<string> = new BehaviorSubject(null);
    readonly sidebar$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    readonly appMenuItems$ = new BehaviorSubject<BarAppMenuItem[]>(null);
    readonly menuItems$: BehaviorSubject<BarMenuItemOptions[]> = new BehaviorSubject([]);
    readonly menuItemsAutoCollapse$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly externalLinkGroups$: BehaviorSubject<BaasExternalLinkGroupOptions[]> = new BehaviorSubject(null);
    readonly strikingActions$: BehaviorSubject<BarActionButtonOptions[]> = new BehaviorSubject(null);
    readonly operations$: BehaviorSubject<BarActionButtonOptions[]> = new BehaviorSubject(null);
    readonly submitActions$: BehaviorSubject<BarActionButtonOptions[]> = new BehaviorSubject(null);
    readonly moreActions$: BehaviorSubject<BarActionButtonOptions[]> = new BehaviorSubject(null);
    readonly contentActions$: BehaviorSubject<BarActionButtonOptions[]> = new BehaviorSubject(null);
    readonly mainAction$: BehaviorSubject<BarActionButtonOptions> = new BehaviorSubject(null);
    /** Loading overlay visible or not. This is used when the app is loaded, but when something is loading (e.g. network, http call). */
    readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly loadingOptions$: BehaviorSubject<BaasLoadingOptions> = new BehaviorSubject(createDefaultBaasLoadingOptions());
    /** Global app loading overlay visible or not. Only to be used in exceptional cases. The loader is mainly used to indicate that the entire app is loading. */
    readonly appLoading$ = new BehaviorSubject(true);
    /** Whether layout http interception is performed */
    readonly interception$ = new BehaviorSubject(true);
    readonly compact$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly touch$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly menuState$: BehaviorSubject<BarMenuState> = new BehaviorSubject('default');
    readonly mobileFriendly$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly appVersion$: BehaviorSubject<string> = new BehaviorSubject(null);
    readonly offcanvasOpened$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly offcanvasPinned$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly offcanvasOptions$: BehaviorSubject<BaasOffcanvasOptions | undefined> = new BehaviorSubject<BaasOffcanvasOptions | undefined>(undefined);
    /** @experimental Device type which can only be used in responsive layout modus. Otherwise the value remains undefined. */
    readonly deviceType$: Observable<BarDeviceType | undefined>;

    private preventMenuStateEvents = false;

    get menuAction(): BarActionButtonOptions {
        const translationText = this.#translate.get(
            this.menuState$.getValue() !== 'closed' ? 'DOMAIN_BAR_LAYOUT.MENU.CLOSE_MENU' : 'DOMAIN_BAR_LAYOUT.MENU.OPEN'
        );
        return {
            action: () => {
                this.menuState$.next(this.menuState$.getValue() !== 'closed' ? 'closed' : this.internalLayout.smallDevice ? 'overlay' : 'pinned');
            },
            title: translationText,
            icon: {
                class: 'fas fa-bars',
                srText: translationText,
                iconOnly: true
            }
        };
    }

    private get internalLayout(): _BarInternalLayoutService {
        return this.#injector.get(_BarInternalLayoutService);
    }

    constructor() {
        this.deviceType$ = this.internalLayout.deviceType$$.asObservable();
    }

    ngOnDestroy(): void {
        // complete all behavior subjects to ensure all subscribers close (avoid leaks)
        this.hidden$.complete();
        this.helpLink$.complete();
        this.title$.complete();
        this.subTitle$.complete();
        this.documentTitle$.complete();
        this.sidebar$.complete();
        this.appMenuItems$.complete();
        this.menuItems$.complete();
        this.menuItemsAutoCollapse$.complete();
        this.externalLinkGroups$.complete();
        this.mainHeader$.complete();
        this.strikingActions$.complete();
        this.operations$.complete();
        this.submitActions$.complete();
        this.moreActions$.complete();
        this.contentActions$.complete();
        this.mainAction$.complete();
        this.loading$.complete();
        this.loadingOptions$.complete();
        this.appLoading$.complete();
        this.interception$.complete();
        this.compact$.complete();
        this.touch$.complete();
        this.menuState$.complete();
        this.mobileFriendly$.complete();
        this.appVersion$.complete();
        this.offcanvasOpened$.complete();
        this.offcanvasPinned$.complete();
        this.offcanvasOptions$.complete();
    }

    init(): void {
        this.documentTitle$.subscribe((title: string) => {
            // only set document title when title is not null, a document title is always required
            if (title) document.title = title;
        });
        this.compact$.subscribe((compact: boolean) => {
            if (compact !== null) this.#gvk.persistPageState(compact ? 'compact' : 'default');
        });
        this.menuState$.subscribe((state) => {
            if (!this.preventMenuStateEvents) {
                this.internalLayout.setMenuState(state);
            }
        });
        this.#gvk.barMenuStateChanged.subscribe((barMenuState: BarMenuState) => {
            // gvk will give us the menu state and we have to assign it to the local variable because the layout is bound to it
            // NOTE in this case we don't want menu state events to avoid cycles
            this.preventMenuStateEvents = true;
            this.menuState$.next(barMenuState);
            this.preventMenuStateEvents = false;
        });
        // hook to gvk internal compact changed because we don't want cyclic dependency
        this.#gvk.compactChanged.subscribe((compact) => {
            this.compact$.next(compact);
        });

        this.hidden$.subscribe((value) => {
            // in case hidden we need to hide the header and footer (embedded modus)
            // we hide the wrapper so that we hide the dynamic html
            const headerWrapper = document.getElementById('flanders-header-wrapper');
            if (headerWrapper) headerWrapper.style.display = value ? 'none' : 'block';
            const footerWrapper = document.getElementById('flanders-footer-wrapper');
            if (footerWrapper) footerWrapper.style.display = value ? 'none' : 'block';
        });

        // Subscribe contentActions for adding padding to the H1 so that the actions don't overlap with the H1
        combineLatest([this.contentActions$, this.mainHeader$]).subscribe(([contentActions, mainHeader]) => {
            const addPaddingToHeader = (padding) => {
                const mainWrapper = document.getElementsByClassName('bar-layout-page-content');
                if (mainWrapper && mainWrapper.length) {
                    const pageHeader = mainWrapper[0].getElementsByTagName('h1');
                    if (pageHeader && pageHeader.length) pageHeader[0].style.paddingRight = padding ? `${padding + 30}px` : '0';
                }
            };

            if (contentActions && contentActions.length && !mainHeader) {
                // Timeout of 0 required for rendering the DOM
                setTimeout(() => {
                    const contentActionsTopElements = document.getElementsByClassName('bar-layout-page-content__actions--top');
                    if (contentActionsTopElements.length) {
                        const contentActionsWidth = contentActionsTopElements[0].getBoundingClientRect().width;
                        addPaddingToHeader(contentActionsWidth);
                    }
                }, 0);
            } else {
                addPaddingToHeader(0);
            }
        });
    }

    reset(options: BarResetLayoutOptions = {}): void {
        // by default we enable the sidebar
        if (options.clearSidebar !== false) {
            this.sidebar$.next(true);
        }
        // clear out values which are specific to a page
        if (options.clearStrikingActions !== false) {
            this.strikingActions$.next(null);
        }
        if (options.clearOperations !== false) {
            this.operations$.next(null);
        }
        if (options.clearExternalLinks !== false) {
            this.externalLinkGroups$.next(null);
        }
        if (options.clearSubmitActions !== false) {
            this.submitActions$.next(null);
        }
        if (options.clearContentActions !== false) {
            this.contentActions$.next(null);
        }
        if (options.clearMoreActions !== false) {
            this.moreActions$.next(null);
        }
        if (options.clearTitle !== false) {
            this.title$.next(null);
        }
        if (options.clearSubTitle !== false) {
            this.subTitle$.next(null);
        }
        if (options.clearMenu !== false) {
            this.menuItems$.next(null);
        }
        if (options.clearAppMenu === true) {
            this.appMenuItems$.next(null);
        }
        if (options.clearHelp !== false) {
            this.helpLink$.next(null);
        }
        if (options.clearInterception !== false) {
            this.interception$.next(true);
        }

        if (options.clearMainHeader !== false) this.mainHeader$.next(null);

        if (options.clearOffcanvas !== false) {
            this.offcanvasOpened$.next(false);
            this.offcanvasPinned$.next(false);
            this.offcanvasOptions$.next(undefined);
        }

        if (this.appLoading$.getValue()) this.appLoading$.next(false);
    }

    /* Inject a component into a section of the layout dynamically and returns an observable with the component ref.
     * Use this component ref to pass additional data.
     * @param component Component to inject into the layout
     * @param section Section within the layout to inject the component into
     */
    injectSection<T>(component: Type<T>, section: BarLayoutSection): Observable<ComponentRef<T>> {
        const prepInjection = (component: Type<T>, section: BarLayoutSection): ComponentRef<T> => {
            const injection = new BarLayoutSectionInjection<T>();
            injection.section = section;
            injection.component = component;
            this.internalLayout.injectSection.emit(injection);
            return injection.componentRef;
        };

        return new Observable<ComponentRef<T>>((subscriber) => {
            const complete = () => {
                const result = prepInjection(component, section);
                subscriber.next(result);
                return subscriber.complete();
            };
            this.internalLayout.queueAction(() => complete());
        });
    }

    /**
     * Clears the given section
     * @param section Given section in the layout.
     */
    clearSection(section: BarLayoutSection): Observable<void> {
        return new Observable<void>((subscriber) => {
            const complete = () => {
                this.internalLayout.clearSection.emit(section);
                subscriber.next();
                subscriber.complete();
            };
            this.internalLayout.queueAction(() => {
                complete();
            });
        });
    }
}
